import React from 'react';
import type { ReactNode, CSSProperties } from 'react';
import useStyles from '../../theme/useStyles';
import DataTable from './DataTable';
import type { Column, RowKey } from './DataTable';

// A list with its toolbar: title on the left, whatever the module puts in
// `actions` on the right, and the table under it.
//
// Printing and spreadsheet export were removed from the application, so there
// is no Print or Export CSV button and no print surface around the table.

export interface TablePanelProps<T> {
  title: ReactNode;
  columns: Array<Column<T>>;
  /** What is on screen — usually the current page. */
  rows: T[];
  onRowClick?: (row: T) => void;
  rowStyle?: (row: T) => CSSProperties | null | undefined;
  rowKey?: RowKey<T>;
  loading?: boolean;
  emptyMessage?: ReactNode;
  minWidth?: string;
  /** Filters, toggles and buttons on the toolbar's right. */
  actions?: ReactNode;
  /** Something between the toolbar and the table (a totals strip, a form). */
  above?: ReactNode;
  /** Below the table — pagination, totals. */
  below?: ReactNode;
  isDarkMode?: boolean;
  style?: CSSProperties;
}

const TablePanel = <T,>({
  title, columns, rows, onRowClick, rowStyle, rowKey, loading, emptyMessage,
  minWidth, actions, above, below, isDarkMode, style
}: TablePanelProps<T>) => {
  const s = useStyles(isDarkMode);
  const t = s.t;

  return (
    <div style={{ marginBottom: t.spacing.lg, ...style }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: '8px', flexWrap: 'wrap', marginBottom: t.spacing.sm
      }}>
        <div style={{ ...s.sectionTitle, margin: 0 }}>{title}</div>
        {actions && (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
            {actions}
          </div>
        )}
      </div>
      {above}
      <div className="ss-table">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={rowKey}
          onRowClick={onRowClick}
          rowStyle={rowStyle}
          loading={loading}
          minWidth={minWidth}
          emptyMessage={emptyMessage}
          isDarkMode={isDarkMode}
        />
      </div>
      {below}
    </div>
  );
};

export default TablePanel;
